import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const EARLIEST_DATE_STRING = "2024-11-06";

function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

const earliestDate = parseLocalDate(EARLIEST_DATE_STRING);

const getMonthOptions = () => {
  // Generate months from Nov 2024 to 12 months ahead
  const months = [];
  let d = new Date(earliestDate);
  for (let i = 0; i < 7; i++) {
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    months.push({
      value: `${year}-${month.toString().padStart(2, "0")}`,
      label: d.toLocaleString("default", { month: "short", year: "numeric" }),
    });
    d.setMonth(d.getMonth() + 1);
  }
  return months;
};

const DateFilterSection = ({ value, onChange, onClear }) => {
  const [dateType, setDateType] = useState(value?.type || "month");
  const [startDate, setStartDate] = useState(value?.startDate || "");
  const [endDate, setEndDate] = useState(value?.endDate || "");
  const [selectedMonths, setSelectedMonths] = useState(value?.months || []);

  useEffect(() => {
    setDateType(value?.type || "month");
    setStartDate(value?.startDate || "");
    setEndDate(value?.endDate || "");
    setSelectedMonths(value?.months || []);
  }, [value]);

  const handleDateTypeChange = (type) => {
    setDateType(type);
    if (type === "range") {
      setSelectedMonths([]);
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleClearDate = () => {
    setStartDate("");
    setEndDate("");
    setSelectedMonths([]);
    setDateType("month");
    onClear && onClear();
  };

  const monthOptions = getMonthOptions();

  const today = new Date();

  return (
    <div>
      <h2 className="font-bold text-lg mb-4">Filter by Date</h2>
      <div className="flex gap-4 mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="dateType"
            value="month"
            checked={dateType === "month"}
            onChange={() => handleDateTypeChange("month")}
            className="peer hidden"
          />
          <span className="w-4 h-4 rounded-full border border-mblue-700 flex items-center justify-center peer-checked:bg-mblue-700 peer-checked:border-mblue-700 transition">
            {dateType === "month" && (
              <span className="w-2 h-2 bg-white rounded-full block"></span>
            )}
          </span>
          <span>Month</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="dateType"
            value="range"
            checked={dateType === "range"}
            onChange={() => handleDateTypeChange("range")}
            className="peer hidden"
          />
          <span className="w-4 h-4 rounded-full border border-mblue-700 flex items-center justify-center peer-checked:bg-mblue-700 peer-checked:border-mblue-700 transition">
            {dateType === "range" && (
              <span className="w-2 h-2 bg-white rounded-full block"></span>
            )}
          </span>
          <span>Date Range</span>
        </label>
      </div>
      {dateType === "range" ? (
        <div className="flex flex-col gap-2 mb-2">
          <div>
            <label className="block text-sm mb-1">Start</label>
            <DatePicker
              selected={parseLocalDate(startDate)}
              onChange={(date) => {
                const iso = date ? date.toISOString().slice(0, 10) : "";
                setStartDate(iso);
                onChange &&
                  onChange({
                    type: "range",
                    startDate: iso,
                    endDate,
                    months: [],
                  });
              }}
              selectsStart
              startDate={parseLocalDate(startDate)}
              endDate={parseLocalDate(endDate)}
              minDate={earliestDate}
              maxDate={today}
              dateFormat="yyyy-MM-dd"
              className="px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-msky-500 focus:border-transparent w-full"
              placeholderText="Select start date"
              disabled={dateType !== "range"}
            />
          </div>
          <div>
            <label className="block text-sm mb-1">End</label>
            <DatePicker
              selected={parseLocalDate(endDate)}
              onChange={(date) => {
                const iso = date ? date.toISOString().slice(0, 10) : "";
                setEndDate(iso);
                onChange &&
                  onChange({
                    type: "range",
                    startDate,
                    endDate: iso,
                    months: [],
                  });
              }}
              selectsEnd
              startDate={parseLocalDate(startDate)}
              endDate={parseLocalDate(endDate)}
              minDate={
                parseLocalDate(startDate)
                  ? parseLocalDate(startDate)
                  : earliestDate
              }
              dateFormat="yyyy-MM-dd"
              className="px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-msky-500 focus:border-transparent w-full"
              placeholderText="Select end date"
              disabled={dateType !== "range"}
            />
          </div>
        </div>
      ) : (
        <div className="mb-2">
          <Select
            isMulti
            options={monthOptions}
            value={monthOptions.filter((m) => selectedMonths.includes(m.value))}
            onChange={(selected) => {
              const newMonths = selected ? selected.map((m) => m.value) : [];
              setSelectedMonths(newMonths);
              onChange &&
                onChange({
                  type: "month",
                  startDate: null,
                  endDate: null,
                  months: newMonths,
                });
            }}
            isDisabled={dateType !== "month"}
            classNamePrefix="react-select"
            placeholder="Select month(s)..."
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 20 }),
              control: (provided) => ({ ...provided, minHeight: "40px" }),
              multiValue: (provided) => ({
                ...provided,
                backgroundColor: "#157787",
                borderRadius: "9999px",
                padding: "2px 6px",
              }),
              multiValueLabel: (provided) => ({
                ...provided,
                color: "white",
                fontWeight: 500,
                padding: "0 6px",
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: "white",
                ":hover": {
                  backgroundColor: "#157787",
                  color: "white",
                },
                borderRadius: "9999px",
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                  ? "#157787"
                  : state.isFocused
                    ? "#e8f1f3"
                    : "white",
                color: state.isSelected ? "white" : "black",
                fontWeight: state.isSelected ? 600 : 400,
              }),
            }}
          />
        </div>
      )}
      <button
        className="mt-2 text-xs text-mblue-700 underline hover:text-mblue-900"
        onClick={handleClearDate}
      >
        Clear Date Filter
      </button>
    </div>
  );
};

export default DateFilterSection;
